import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Shield, Key } from "lucide-react";
import * as OTPAuth from "otpauth";

interface MFAVerificationProps {
  userId: string;
  userEmail: string;
  onVerified: () => Promise<void>;
  onCancel?: () => void;
}

export const MFAVerification = ({ userId, userEmail, onVerified, onCancel }: MFAVerificationProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const verifyTOTP = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a 6-digit code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Get MFA settings from database
      const { data: mfaSettings, error: settingsError } = await supabase
        .from('user_mfa_settings')
        .select('mfa_secret')
        .eq('user_id', userId)
        .eq('mfa_enabled', true)
        .single();

      if (settingsError || !mfaSettings || !mfaSettings.mfa_secret) {
        throw new Error('MFA not configured for this account');
      }

      // Verify the TOTP code
      const totp = new OTPAuth.TOTP({
        issuer: 'Church App',
        label: userEmail,
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
        secret: OTPAuth.Secret.fromBase32(mfaSettings.mfa_secret),
      });

      const delta = totp.validate({ token: verificationCode, window: 1 });
      
      if (delta === null) {
        // Log failed attempt
        await supabase.rpc('log_mfa_attempt', {
          _user_id: userId,
          _success: false,
          _attempt_type: 'totp'
        });

        toast({
          title: "Invalid code",
          description: "The code you entered is incorrect. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Log successful verification
      await supabase.rpc('log_mfa_attempt', {
        _user_id: userId,
        _success: true,
        _attempt_type: 'totp'
      });

      toast({
        title: "Verified",
        description: "Two-factor authentication successful",
      });

      await onVerified();
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message || "Failed to verify code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyBackupCode = async () => {
    if (!verificationCode || verificationCode.length !== 8) {
      toast({
        title: "Invalid backup code",
        description: "Backup codes are 8 characters long",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: isValid, error } = await supabase.rpc('verify_backup_code', {
        _user_id: userId,
        _code: verificationCode.toUpperCase()
      });

      if (error) throw error;

      if (isValid) {
        toast({
          title: "Verified",
          description: "Backup code accepted. This code has been used and cannot be reused.",
        });
        onVerified();
      } else {
        toast({
          title: "Invalid code",
          description: "The backup code you entered is incorrect or has already been used.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Verification failed",
        description: error.message || "Failed to verify backup code",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = () => {
    if (useBackupCode) {
      verifyBackupCode();
    } else {
      verifyTOTP();
    }
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Enter the {useBackupCode ? 'backup code' : '6-digit code'} from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Key className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {useBackupCode 
              ? 'Enter one of your 8-character backup codes. Each code can only be used once.'
              : 'Open your authenticator app and enter the current 6-digit code.'
            }
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="mfa-code" className="text-foreground">
            {useBackupCode ? 'Backup Code' : 'Verification Code'}
          </Label>
          <Input
            id="mfa-code"
            type="text"
            maxLength={useBackupCode ? 8 : 6}
            placeholder={useBackupCode ? 'ABCD1234' : '000000'}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
            className="text-center text-2xl tracking-widest font-mono bg-background text-foreground border-input"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Button
            onClick={handleVerify}
            disabled={loading || (useBackupCode ? verificationCode.length !== 8 : verificationCode.length !== 6)}
            className="w-full"
          >
            Verify
          </Button>

          <Button
            variant="ghost"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setVerificationCode('');
            }}
            className="w-full text-sm"
          >
            {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
          </Button>

          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              className="w-full text-sm"
            >
              Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
